import { useCallback, useMemo, useState } from 'react'

// Backend is the source of truth: any language that has at least one stored
// translation is automatically "declared". This hook adds a transient pending
// list for languages the user has clicked "Add" on but not yet typed into —
// they appear in the sidebar immediately but disappear on reload unless a
// translation is saved. Once a translation is stored, languagesInUse picks
// it up and persistence is owned by the API.
//
// For multi-market surveys (and single-market with multiple language
// allocations), the sidebar also seeds from `availableLanguages` — the list
// of allocated-but-not-yet-translated languages — so every allocated locale
// appears at 0% automatically. This surfaces launch-gate warnings before the
// researcher has typed anything.

interface Options {
  primaryLanguage: string
  // Languages that have at least one stored translation (computed from the
  // fetched questionnaire + audience data).
  derivedFromTranslations: ReadonlySet<string>
  // Languages allocated on the Audience page (single-survey) or across all
  // markets (multi-market), minus English. Seeding from this ensures allocated
  // languages appear immediately at 0% without manual "+ Add language".
  availableLanguages?: ReadonlyArray<{ code: string; name?: string }>
}

export interface DeclaredLanguagesApi {
  languages: ReadonlyArray<string>
  addLanguage: (code: string) => void
  // Drops the language from the transient pending list. Backend cleanup for
  // languages already in `derivedFromTranslations` is the caller's
  // responsibility — invoke a delete-translations workflow before calling
  // this, otherwise the language will reappear on the next data refresh.
  forgetLanguage: (code: string) => void
}

export const useDeclaredLanguages = ({
  primaryLanguage,
  derivedFromTranslations,
  availableLanguages = []
}: Options): DeclaredLanguagesApi => {
  const [pending, setPending] = useState<ReadonlyArray<string>>([])

  const languages = useMemo(() => {
    const set = new Set<string>(pending)
    derivedFromTranslations.forEach((c) => set.add(c))
    // Seed from allocated languages so they appear at 0% without manual add.
    // This is safe because the launch gate is driven by stored translations
    // and allocations (useHasStoredTranslations / useMarketsTranslationWarnings),
    // not by the sidebar's declared list — adding a language here does not
    // make the survey "incomplete" if there are no stored translations.
    for (const lang of availableLanguages) {
      set.add(lang.code)
    }
    set.delete(primaryLanguage)
    return Array.from(set)
  }, [pending, derivedFromTranslations, availableLanguages, primaryLanguage])

  const addLanguage = useCallback(
    (code: string) => {
      if (!code || code === primaryLanguage) return
      setPending((prev) => (prev.includes(code) ? prev : [...prev, code]))
    },
    [primaryLanguage]
  )

  const forgetLanguage = useCallback((code: string) => {
    setPending((prev) => prev.filter((c) => c !== code))
  }, [])

  return { languages, addLanguage, forgetLanguage }
}
